/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2022 (original work) Open Assessment Technologies SA
 */
import { __ } from '@oat-sa-private/ui-core';
import moment from 'moment';
import getInteractionUserName from '@/core/utils/getInteractionUserName';
import { timerLevels } from '@/core/utils/consts';
import { formatMomentDatetime } from '@/core/utils/date.js';

/**
 * interaction.positionLabel is used to display interaction position on monitoring page.
 * This function builds positionLabel property when payload is received.
 * @param {Object} interaction - interaction to parse
 */
export function parsePositionLabel(interaction) {
    const position = interaction.position;
    if (position) {
        interaction.positionLabel = '';
        interaction.positionLabelSr = '';
        if (position.part) {
            interaction.positionLabel += `${__('part')} ${position.part} - `;
            interaction.positionLabelSr += `${__('part')} ${position.part} - `;
        }
        if (position.informationalIndex) {
            //U+1D456 for 'i', which looks more like icon and won't be uppercased
            interaction.positionLabel += `𝒊.${position.informationalIndex}`;
            interaction.positionLabelSr += `${__('informational')} ${position.informationalIndex}`;
        } else if (position.item && position.total) {
            interaction.positionLabel += `${position.item}/${position.total}`;
            interaction.positionLabelSr += `${__('item %s of %s', position.item, position.total)}`;
        }
    }
}

/**
 * `interaction.currentTimerLevel` is used to display current timer info on monitoring/details page.
 * This function builds `currentTimerLevel` property when payload is received.
 * Current timer:
 *     among timers applied to the test-taker's current position context, timer with the smallest value;
 *     'extra' timer is not counted, only normal timers
 * @param {Object} interaction - interaction to parse
 */
export function parseCurrentTimerLevel(interaction) {
    let entries;
    if (interaction.timers) {
        entries = Object.entries(interaction.timers)
            .filter(a => a[0] !== timerLevels.extra && a[1])
            .sort((a, b) => a[1].maxTimeRemaining - b[1].maxTimeRemaining);
    }
    interaction.currentTimerLevel = entries && entries.length ? entries[0][0] : null;
}

/**
 * Converts the timestamp to the formatted time of submission
 * @param {string} endTs - timestamp of the end or the current timestamp (RFC3339)
 * @returns {String} time of submission of the test
 */
export function timestampToFinishTime(endTs) {
    return formatMomentDatetime(endTs);
}

/**
 * Sorts a given array of interactions by a given criterion
 * @param {Array} interactions - Array of interactions to sort
 * @param {String} sortBy - Property to sort by
 * @returns {Array} - Sorted interactions array
 */
export function sortInteractionsBy(interactions, sortBy) {
    switch (sortBy) {
        case 'user.name':
            interactions = interactions.sort((a, b) => sortByUserName(a, b));
            break;
        case 'positionLabel':
        case 'ipAddress':
        case 'title':
            interactions = interactions.sort((a, b) => {
                if (!a[sortBy]) {
                    return 1;
                }
                if (!b[sortBy]) {
                    return -1;
                }
                return a[sortBy].localeCompare(b[sortBy]);
            });
            break;
        case 'status':
            interactions = interactions.sort((a, b) => {
                // if value is the same, we sort by TT name
                const statusA = a.deliveryExecution && a.deliveryExecution.status;
                const statusB = b.deliveryExecution && b.deliveryExecution.status;
                if (statusA === statusB) {
                    return sortByUserName(a, b);
                }
                if (typeof statusA === 'undefined') {
                    return 1;
                }
                if (typeof statusB === 'undefined') {
                    return -1;
                }
                return statusA.localeCompare(statusB); //not translated
            });
            break;
        case 'progressPercentage':
            interactions = interactions.sort((a, b) => {
                // if completion is the same, we sort by TT name
                if (a[sortBy] === b[sortBy]) {
                    return sortByUserName(a, b);
                }
                if (typeof a[sortBy] === 'undefined') {
                    return 1;
                }
                if (typeof b[sortBy] === 'undefined') {
                    return -1;
                }
                return a[sortBy] - b[sortBy];
            });
            break;
        case 'endTs':
            // time of submit, earlier is first
            interactions = interactions.sort((a, b) => {
                const endTsA = (a.deliveryExecution && a.deliveryExecution.endTs) || moment().format();
                const endTsB = (b.deliveryExecution && b.deliveryExecution.endTs) || moment().format();

                return moment(endTsA) - moment(endTsB);
            });
            break;
        case 'startTs':
            // time spent, less is first
            interactions = interactions.sort((a, b) => {
                const startTsA = (a.deliveryExecution && a.deliveryExecution.startTs) || moment().format();
                const startTsB = (b.deliveryExecution && b.deliveryExecution.startTs) || moment().format();
                const endTsA = (a.deliveryExecution && a.deliveryExecution.endTs) || moment().format();
                const endTsB = (b.deliveryExecution && b.deliveryExecution.endTs) || moment().format();

                return moment(endTsA).diff(moment(startTsA)) - moment(endTsB).diff(moment(startTsB));
            });
            break;
        case 'additionalTime':
            interactions = interactions.sort((a, b) => {
                const extraA = a.timers && a.timers[timerLevels.extra] ? a.timers[timerLevels.extra].maxTime : 0;
                const extraB = b.timers && b.timers[timerLevels.extra] ? b.timers[timerLevels.extra].maxTime : 0;
                // if value is the same, we sort by TT name
                if (extraA === extraB) {
                    return sortByUserName(a, b);
                }
                return extraA - extraB;
            });
            break;
    }

    return interactions;
}

/**
 * Sorts two interactions comparing its user name. This function is called
 * inside Array sort so it must return expected values to be used by it
 * @param {Object} interactionA - Interaction A to compare
 * @param {Object} interactionB - Interaction B to compare
 * @returns {Integer} - -1 if interaction A must go first, 1 if interaction B must go second, 0 if order must not change
 */
function sortByUserName(interactionA, interactionB) {
    const userNameA = getInteractionUserName(interactionA.user).toLowerCase();
    const userNameB = getInteractionUserName(interactionB.user).toLowerCase();

    return userNameA.localeCompare(userNameB);
}
